import { LinearGradient } from 'expo-linear-gradient';
import { router } from 'expo-router';
import { isEmpty } from 'lodash';
import * as React from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
    BodyMediumEmphasized,
    BodyMediumRegular,
    BodySmallRegular,
    CloseButton,
    H5Emphasized,
    HelpCircleIcon,
    isWeb,
    SafeAreaView,
    ScrollView,
    Separator,
    tokens,
    View,
    XStack,
    YStack,
} from 'smoothie';

import {
    convertCategoryToIcon,
    getCategoryDisplayString,
    getCategoryExampleString,
} from '../../common/utils/billUtils';
import { BillCategory, BillCategoryBackgroundColors } from '../types/Bill';
import { BillDetailIcon, BillIconSize } from './common/BillIcons';
import { TEXT_FOR_UNSET_VALUES } from './common/BillRows';

export const BillCategoryRow = ({
    categoryDescription,
}: {
    categoryDescription?: string;
}) => {
    const text =
        categoryDescription !== undefined && !isEmpty(categoryDescription)
            ? getCategoryDisplayString(categoryDescription)
            : TEXT_FOR_UNSET_VALUES;
    const category = !isEmpty(categoryDescription)
        ? (categoryDescription as BillCategory)
        : BillCategory.OTHER;
    const example = getCategoryExampleString(category);
    const icon = convertCategoryToIcon(category) ?? HelpCircleIcon;
    const color =
        BillCategoryBackgroundColors[category] ??
        tokens.color.light.gray.gray100;
    const iconComponent = (
        <BillDetailIcon color={color} icon={icon} size={BillIconSize.MEDIUM} />
    );

    return (
        <>
            {example && (
                <>
                    <XStack gap={12} paddingVertical={16} alignItems="center">
                        {iconComponent}
                        <YStack>
                            <BodyMediumEmphasized>{text}</BodyMediumEmphasized>
                            <BodySmallRegular
                                color="$gray1000"
                                numberOfLines={1}
                            >
                                Ex. {example}.
                            </BodySmallRegular>
                        </YStack>
                    </XStack>
                    <Separator />
                </>
            )}
        </>
    );
};

export const BillsCategories = () => {
    return (
        <View paddingBottom={28}>
            {Object.values(BillCategory).map((category, i) => (
                <BillCategoryRow key={i} categoryDescription={category} />
            ))}
        </View>
    );
};

const BillsCategoryInfo = () => {
    const { bottom } = useSafeAreaInsets();

    return (
        <SafeAreaView
            edges={['top', 'left', 'right']}
            style={{
                flex: 1,
            }}
        >
            <YStack
                paddingVertical={isWeb ? 24 : undefined}
                height="100%"
                maxWidth={700}
                alignSelf="center"
                justifyContent="space-between"
            >
                <YStack flex={1} paddingHorizontal={24}>
                    <YStack>
                        <CloseButton
                            paddingHorizontal={5}
                            onPress={router.back}
                        />
                    </YStack>
                    <ScrollView
                        marginTop={4}
                        showsVerticalScrollIndicator={isWeb}
                        contentContainerStyle={{ paddingBottom: bottom }}
                    >
                        <H5Emphasized paddingTop={28}>
                            Fruitful defines all of the following recurring
                            expenses as bills
                        </H5Emphasized>
                        <YStack paddingTop={28}>
                            <BillsCategories />
                        </YStack>
                        <YStack paddingBottom={20}>
                            <BodyMediumRegular color="$gray1000">
                                We wouldn’t include things like one-off medical
                                or vet bills, recurring savings contributions,
                                or an expense that has a limited number of
                                payments.
                            </BodyMediumRegular>
                        </YStack>
                    </ScrollView>
                    <LinearGradient
                        colors={[
                            'rgba(255, 255, 255, 0)',
                            tokens.color.light.brand.white,
                        ]}
                        style={{
                            height: 20,
                            width: '100%',
                            position: 'absolute',
                            bottom: 0,
                        }}
                        pointerEvents="none"
                    />
                </YStack>
            </YStack>
        </SafeAreaView>
    );
};

export default BillsCategoryInfo;
